import isEmpty from 'lodash/isEmpty';
import React, { FC, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useQueryClient } from 'react-query';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router';
import { ProjectId, Role, Team } from 'api/models';
import { Form } from 'components/atoms/form';
import { Option } from 'components/molecules/squad-picker/types';
import { toastService } from 'components/molecules/toast';
import {
  RolesDrawer,
  RoleSettingsTab,
  RolesForm,
} from 'components/organisms/module-settings/roles';
import { useTranslate } from 'hooks/use-translation';
import {
  getTeamsInAccountQueryKey,
  useCreatePlaceholderMutation,
  useDeletePlaceholderMutation,
  useDeletePlaceholdersByRoleMutation,
  useUpdatePlaceholderMutation,
} from 'queries/placeholder';
import { ROLES_QUERY_KEY, useUpdateRoleMutation } from 'queries/roles';
import { removeProjectPlaceholder } from 'stores/memberships';
import {
  add as addTeam,
  remove as removeTeam,
  update as updateTeam,
} from 'stores/teams';
import { getRoleTypeForTab } from '.';

interface Params {
  roleId: string;
}

interface Props {
  roles: Role[];
  placeholders: { [id: number]: Team };
  onClose: VoidFunction;
  view: RoleSettingsTab;
}

export const UpdateRole: FC<Props> = ({
  roles,
  placeholders,
  onClose,
  view,
}) => {
  const { roleId } = useParams<Params>();
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const translate = useTranslate();

  const _roleId = Number(roleId);
  const [form] = Form.useForm();

  const [initialState, setInitialState] =
    useState<null | {
      roleName: string;
      defaultPlaceholderId?: number;
    }>(null);

  const { mutateAsync: createPlaceholder } = useCreatePlaceholderMutation({
    onSuccess: async (team) => {
      dispatch(addTeam(team));
      queryClient.setQueryData<Team[]>(
        getTeamsInAccountQueryKey(),
        (oldValues = []) => [
          ...oldValues.filter(({ teamId }) => teamId !== team.teamId),
          team,
        ],
      );
    },
  });

  const { mutateAsync: deletePlaceholder } = useDeletePlaceholderMutation(
    initialState?.defaultPlaceholderId as number,
    {
      onSuccess: async () => {
        dispatch(removeTeam(initialState?.defaultPlaceholderId as number));
        queryClient.setQueryData<Team[]>(
          getTeamsInAccountQueryKey(),
          (oldValues = []) => [
            ...oldValues.filter(
              ({ teamId }) =>
                teamId !== (initialState?.defaultPlaceholderId as number),
            ),
          ],
        );
      },
    },
  );
  const { mutateAsync: deletePlaceholdersByTeam } =
    useDeletePlaceholdersByRoleMutation({
      onSuccess(_, { roleId: removedRoleId }) {
        const teamsForRole = (
          queryClient.getQueryData<Team[]>(getTeamsInAccountQueryKey()) ?? []
        ).filter(({ roleId: teamRoleId }) => teamRoleId === removedRoleId);

        teamsForRole.forEach(({ teamId, projectId }) => {
          dispatch(removeTeam(teamId));
          if (projectId != null) {
            dispatch(
              removeProjectPlaceholder({
                projectId: ProjectId(projectId),
                teamId,
              }),
            );
          }
        });
        queryClient.setQueryData<Team[]>(
          getTeamsInAccountQueryKey(),
          (oldValues = []) => [
            ...oldValues.filter(
              ({ roleId: teamRoleId }) => teamRoleId !== removedRoleId,
            ),
          ],
        );
        queryClient.invalidateQueries({ queryKey: ['placeholders'] });
      },
    });

  const { mutateAsync: updatePlaceholder } = useUpdatePlaceholderMutation(
    initialState?.defaultPlaceholderId as number,
    {
      onSuccess: async (team) => {
        dispatch(updateTeam(team));
        queryClient.setQueryData<Team[]>(
          getTeamsInAccountQueryKey(),
          (oldValues = []) => [
            ...oldValues.filter(({ teamId }) => teamId !== team.teamId),
            team,
          ],
        );
      },
    },
  );

  const { mutateAsync: updateRole, isLoading } = useUpdateRoleMutation(
    _roleId,
    {
      onSuccess: async (_role) => {
        queryClient.invalidateQueries(ROLES_QUERY_KEY);
      },
    },
  );

  const onSubmit = async () => {
    const values = await form.validateFields();

    try {
      await updateRole(values);

      // Check for name change
      if (
        initialState?.roleName !== values.roleName &&
        values.use_as_placeholder
      ) {
        await updatePlaceholder({
          name: values.roleName,
          role: {
            value: _roleId,
          } as unknown as Option,
        });
      }

      // Check for checkbox change
      if (
        Boolean(initialState?.defaultPlaceholderId) !==
        values.use_as_placeholder
      ) {
        if (values.use_as_placeholder) {
          await createPlaceholder({
            name: values.roleName,
            role: {
              value: _roleId,
              label: values.roleName,
              extra: {
                type: values.type.value,
              },
            } as unknown as Option,
          });
        } else {
          await deletePlaceholder();
          await deletePlaceholdersByTeam({ roleId: _roleId });
        }
      }
      toastService({
        title: translate('settings.role.updated.success.title'),
        message: translate('settings.role.updated.success.message'),
      }).success();
    } finally {
      onClose();
    }
  };

  useEffect(() => {
    if (
      initialState !== null ||
      roles.length <= 0 ||
      // Assuming there will be atleast 1 default placeholder for Customer role.
      isEmpty(placeholders) ||
      Object.keys(placeholders).length <= 0
    ) {
      return;
    }
    const targetRole = roles.find((_role) => _role.roleId === _roleId);

    if (!targetRole) {
      return;
    }

    const defaultPlaceholder = Object.values(placeholders).find(
      (_placeholder) => _placeholder.roleId === targetRole.roleId,
    );

    form.setFieldsValue({
      roleName: targetRole.roleName,
      use_as_placeholder: Boolean(defaultPlaceholder),
      type: getRoleTypeForTab(view),
      usedByPartners: targetRole.usedByPartners,
    });

    setInitialState({
      roleName: targetRole.roleName,
      defaultPlaceholderId: defaultPlaceholder?.teamId,
    });
  }, [_roleId, form, roles, placeholders, initialState, translate, view]);

  return (
    <RolesDrawer
      title={<FormattedMessage id="settings.roles.edit" />}
      onClose={onClose}
      onSave={onSubmit}
      isSubmitting={isLoading}
      form={
        <RolesForm
          roles={roles}
          form={form}
          view={view}
          isEdit
          roleId={_roleId}
        />
      }
    />
  );
};
